import {
  PieChart as DashboardIcon,
  Users as UserIcon,
  Monitor as ReportIcon,
  Database as DatabaseIcon,
  // Grid as GroupIcon,
  Truck as TruckIcon,
  CreditCard as CardIcon,
  Zap as DispenserIcon,
  Map as MapIcon,
  Aperture as ApertureIcon,
  Calendar as ApprovalIcon,
  Activity as FleetIcon,
  Bell as NotificationIcon,
  Tool as SupportIcon
} from 'react-feather';
import { USER_ROLES } from './constants';
import { isFleetOnlyModeEnabled } from './appMode';
import { isTyreManagementV2Enabled } from './tyreManagementFeature';

const ROUTE = {
  login: '/login',
  register: '/register',
  app: '/app',
  dashboard: '/app/dashboard',
  support: '/app/support',
  fleetManagement: '/app/fleet-management',
  account: '/app/account',
  sites: '/app/sites',
  siteDetails: '/app/sites/:siteId',
  tanks: '/app/tanks',
  tankDetails: '/app/tanks/:tankId',
  profile: '/app/profile',
  settings: '/app/settings',
  notifications: '/app/notifications',
  cards: '/app/vehicles/cards',
  cardDetails: '/app/vehicles/cards/:cardId',
  cardApprovals: '/app/card-approvals',
  cardRequests: '/app/card-requests',
  reports: '/app/reports',
  transactions: '/app/reports/transactions',
  transactionDetails: '/app/reports/transactions/:transactionId',
  otherReports: '/app/reports/others',
  reconciliationReport: '/app/reports/reconciliation',
  vehicleManagement: '/app/vehicles',
  vehicles: '/app/vehicles/vehicle-list',
  vehicleCreate: '/app/vehicles/vehicle-list/new',
  vehicleStatusList: '/app/vehicle-status-list',
  vehicleRecords: '/app/vehicles/records',
  vehicleDetails: '/app/vehicles/vehicle-list/:vehicleId',
  vehicleEdit: '/app/vehicles/vehicle-list/:vehicleId/edit',
  vehicleBreakdown: '/app/vehicle-breakdown',
  wailonTransactions: '/app/reports/sensor-transactions',
  groups: '/app/vehicles/groups/',
  dispensers: '/app/dispensers',
  dispenserDetails: '/app/dispensers/:dispenserId',
  userManagement: '/app/users',
  requestors: '/app/users/requestors',
  requestorDetails: '/app/users/requestors/:userId',
  operators: '/app/users/operators',
  operatorDetails: '/app/users/operators/:userId',
  drivers: '/app/users/vehicle-drivers',
  driverDetails: '/app/users/vehicle-drivers/:driverId',
  approvers: '/app/users/approvers',
  approverDetails: '/app/users/approvers/:userId',
  siteManagers: '/app/users/site-supervisor',
  siteManagerDetails: '/app/users/site-supervisor/:userId',
  tyreManangement: '/app/vehicles/tyre-management',
  tyreDetails: '/app/vehicles/tyre-management/:tyreId',
  tyreManagementV2: '/app/vehicles/tyre-management-v2',
  tyreV2Overview: '/app/vehicles/tyre-management-v2/overview',
  tyreV2Vehicles: '/app/vehicles/tyre-management-v2/vehicles',
  tyreV2Inventory: '/app/vehicles/tyre-management-v2/inventory',
  tyreV2InventoryScan: '/app/vehicles/tyre-management-v2/inventory/scan/:token',
  tyreV2Retreading: '/app/vehicles/tyre-management-v2/retreading',
  tyreV2Alerts: '/app/vehicles/tyre-management-v2/alerts',
  tyreV2Reports: '/app/vehicles/tyre-management-v2/reports',
  tyreV2Analytics: '/app/vehicles/tyre-management-v2/analytics',
  tyreV2Setup: '/app/vehicles/tyre-management-v2/setup',
};

export const getAppRoutes = (user, config) => {
  const isAdmin = user?.role === USER_ROLES.admin;
  const isSupervisor = user?.role === USER_ROLES.siteManager;
  const isRequestor = user?.role === USER_ROLES.requestor;
  const isApprover = user?.role === USER_ROLES.approver;
  const isOperator = user?.role === USER_ROLES.operator;
  const isSupport = user?.role === USER_ROLES.support;
  const isFleetOnlyMode = isFleetOnlyModeEnabled(config);
  const tyreManagementV2Enabled = isTyreManagementV2Enabled(config);

  if (isSupport) {
    return [
      {
        href: ROUTE.support,
        icon: SupportIcon,
        title: 'Support'
      }
    ];
  }

  if (isOperator) {
    return [
      {
        href: ROUTE.cardApprovals,
        icon: ApprovalIcon,
        title: 'Card Limit Requests'
      }
    ];
  }

  if (isFleetOnlyMode) {
    return [
      {
        href: ROUTE.dashboard,
        icon: DashboardIcon,
        title: 'Dashboard'
      },
      {
        href: ROUTE.fleetManagement,
        icon: FleetIcon,
        title: 'Fleet Management',
        hidden: !config?.fleet_management
      },
      {
        href: ROUTE.sites,
        icon: MapIcon,
        title: 'Sites',
        hidden: !isAdmin
      },
      {
        href: ROUTE.vehicleManagement,
        icon: TruckIcon,
        hidden: isRequestor || isApprover,
        title: 'Vehicle Management',
        subMenu: [
          {
            href: ROUTE.vehicles,
            title: 'Vehicles',
          },
          {
            href: ROUTE.groups,
            icon: ApertureIcon,
            title: 'Groups & Departments',
          }
        ]
      },
      {
        href: ROUTE.transactions,
        icon: ReportIcon,
        title: 'Inward Transactions'
      },
      {
        href: ROUTE.tanks,
        icon: DatabaseIcon,
        title: 'Tanks',
        hidden: isRequestor,
      },
      {
        href: ROUTE.userManagement,
        icon: UserIcon,
        hidden: !isAdmin,
        title: 'User Management',
        subMenu: [
          {
            href: ROUTE.siteManagers,
            title: 'Site Supervisors',
          }
        ]
      }
    ];
  }

  const APP_DRAWER_ROUTES = [
    {
      href: ROUTE.dashboard,
      icon: DashboardIcon,
      title: 'Dashboard'
    },
    {
      href: ROUTE.fleetManagement,
      icon: FleetIcon,
      title: 'Fleet Management',
      hidden: !config?.fleet_management
    },
    {
      href: ROUTE.sites,
      icon: MapIcon,
      title: 'Sites'
    },
    {
      href: ROUTE.notifications,
      icon: NotificationIcon,
      title: 'Notifications',
      badge: 'notifications'
    },
    {
      href: ROUTE.cardApprovals,
      icon: ApprovalIcon,
      title: 'Approvals',
      hidden: !config?.approve_request || isSupervisor || isRequestor,
      badge: 'approvals',
    },
    {
      href: ROUTE.vehicleBreakdown,
      title: 'Breakdown',
      hidden: !config?.vehicle_management,
      icon: CardIcon,
    },
    {
      href: ROUTE.vehicleManagement,
      icon: TruckIcon,
      hidden: isRequestor || isApprover,
      title: 'Vehicle Management',
      subMenu: [
        {
          href: ROUTE.cards,
          title: 'Cards',
        },
        {
          href: ROUTE.groups,
          title: 'Groups',
        },
        {
          href: ROUTE.vehicleRecords,
          hidden: !config?.vehicle_diesel_quantity_detail,
          title: 'Records',
        },
        {
          href: ROUTE.vehicles,
          title: 'Vehicles',
        },
        {
          href: tyreManagementV2Enabled ? ROUTE.tyreManagementV2 : ROUTE.tyreManangement,
          icon: ApertureIcon,
          hidden: !(tyreManagementV2Enabled || config?.show_tyre_management),
          title: 'Tyre Management',
        },
      ],
    },
    {
      href: ROUTE.reports,
      icon: ReportIcon,
      title: 'Reports',
      subMenu: [
        {
          href: ROUTE.transactions,
          title: 'Transactions',
        },
        {
          href: ROUTE.reconciliationReport,
          title: 'Reconciliation',
        },
        // {
        //   href: ROUTE.wailonTransactions,
        //   title: 'Sensor Transactions',
        // },
        {
          href: ROUTE.otherReports,
          title: 'Other Reports',
        },
      ]
    },
    {
      href: ROUTE.tanks,
      icon: DatabaseIcon,
      title: 'Tanks',
      hidden: isRequestor,
    },
    {
      href: ROUTE.dispensers,
      icon: DispenserIcon,
      title: 'Dispensers',
      hidden: !(isAdmin || isSupervisor),
    },
    {
      href: ROUTE.cardRequests,
      icon: CardIcon,
      title: 'Card Requests',
      hidden: !config?.approve_request || !isRequestor,
    },
    {
      href: ROUTE.userManagement,
      icon: UserIcon,
      hidden: !isAdmin,
      title: 'User Management',
      subMenu: [
        {
          href: ROUTE.approvers,
          title: 'Approvers',
          hidden: !config?.approve_request
        },
        {
          href: ROUTE.operators,
          title: 'Operators',
        },
        {
          href: ROUTE.requestors,
          title: 'Requesters',
          hidden: !config?.approve_request
        },
        {
          href: ROUTE.siteManagers,
          title: 'Site Supervisors',
        },
        // {
        //   href: ROUTE.drivers,
        //   title: 'Vehicle Drivers',
        // }
      ]
    },
  ];
  return APP_DRAWER_ROUTES;
};

export default ROUTE;
